import { Component, DestroyRef, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PaypalService, PaypalLogDTO } from '../../../core/services/register/servicesPaypal/paypal/paypal.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
@Component({
    selector: 'app-paypal-premium',
    templateUrl: './paypal-premium.component.html',
    styleUrls: ['./paypal-premium.component.scss'],
    imports: [CommonModule, FormsModule, TranslateModule]
})
export class PaypalPremiumComponent implements OnInit, OnChanges {
  @Input() idUser!: number;
  @Input() paymentMode?: 'PREMIUM_MENSUEL' | 'PREMIUM_ANNUEL' | 'PREMIUM_MENSUEL_OFFERT';

  // For internal selection (if not provided by parent)
  internalPaymentMode: 'PREMIUM_MENSUEL' | 'PREMIUM_ANNUEL' | 'PREMIUM_MENSUEL_OFFERT' = 'PREMIUM_ANNUEL';

  loaded = false;
  paypalButtonId = '';
  paypalActionUrl = '';

  private premiumButtonId = '';
  private premiumNoTrialButtonId = '';
  private premiumFreeMonthButtonId = '';

  private destroyRef = inject(DestroyRef);
  private paypalService = inject(PaypalService);

  ngOnInit() {
    this.paypalService.buttonProperties()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (props) => {
          this.premiumButtonId = props.premiumButtonId ?? '';
          this.premiumNoTrialButtonId = props.premiumNoTrialButtonId ?? '';
          this.premiumFreeMonthButtonId = props.premiumFreeMonthButtonId ?? '';
          this.paypalActionUrl = props.paypalActionUrl ?? '';
          this.updateButtonId();
          this.loaded = true;
        },
        error: (err) => {
          console.error('[PayPal][Premium] Error fetching button properties:', err);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update button ID when payment mode changes
    if (changes['paymentMode'] && this.loaded) {
      this.updateButtonId();
    }
    // Sync internalPaymentMode with paymentMode input if provided
    if (changes['paymentMode'] && this.paymentMode) {
      this.internalPaymentMode = this.paymentMode;
    }
  }

  private updateButtonId() {
    this.paypalButtonId = this.getButtonId();
  }

  private getButtonId(): string {
    if (this.effectivePaymentMode === 'PREMIUM_MENSUEL_OFFERT') {
      return this.premiumFreeMonthButtonId || this.premiumNoTrialButtonId;
    }
    // For Mensuel and Annuel: use the button with both options configured
    if (this.premiumNoTrialButtonId) {
      return this.premiumNoTrialButtonId;
    }
    return this.premiumButtonId;
  }

  get effectivePaymentMode() {
    return this.paymentMode ?? this.internalPaymentMode;
  }

  /**
   * Get the os0 value for PayPal form submission
   * PayPal buttons use "Mensuel" or "Annuel" as configured in the button setup
   */
  private getOs0Value(): string {
    return this.effectivePaymentMode === 'PREMIUM_ANNUEL' ? 'Annuel' : 'Mensuel';
  }

  /**
   * Get the on0 label for PayPal form submission
   */
  private getOn0Value(): string {
    return this.effectivePaymentMode === 'PREMIUM_MENSUEL_OFFERT'
      ? 'Budgetfacile Premium *1 mois offert'
      : 'Souscription-Budgetfacile-Premium';
  }

  subscribe() {
    if (!this.idUser || this.idUser == 0) {
      return;
    }
    
    const buttonId = this.getButtonId();
    if (!buttonId || buttonId.trim() === '') {
      alert('Erreur: Le bouton PayPal n\'est pas configuré. Veuillez contacter le support.');
      return;
    }

    if (!this.paypalActionUrl || this.paypalActionUrl.trim() === '') {
      alert('Erreur: L\'URL PayPal n\'est pas configurée. Veuillez contacter le support.');
      return;
    }
    
    const paymentMode = this.effectivePaymentMode === 'PREMIUM_ANNUEL' ? 'ANNUEL' : 'MENSUEL';
    
    const log: PaypalLogDTO = {
      actionType: 'SUBSCRIPTION',
      idUser: this.idUser,
      paymentMode: paymentMode,
      paypalButtonId: buttonId,
      subscriptionType: 'PREMIUM'
    };
    
    this.paypalService.logPublic(log)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitPaypalForm(buttonId);
        },
        error: (err) => {
          console.error('[PayPal][Premium] Error logging subscription:', err);
          this.submitPaypalForm(buttonId);
        }
      });
  }

  private submitPaypalForm(buttonId: string) {
          const form = document.createElement('form');
          form.method = 'post';
          form.target = '_top';
          form.action = this.paypalActionUrl;

    const returnUrl = `${window.location.origin}/authentication/login`;

    const fields: { name: string; value: string }[] = [
      { name: 'cmd', value: '_s-xclick' },
      { name: 'hosted_button_id', value: buttonId },
      { name: 'on0', value: this.getOn0Value() },
      { name: 'os0', value: this.getOs0Value() },
      { name: 'currency_code', value: 'EUR' },
      { name: 'custom', value: String(this.idUser) },
      { name: 'return', value: returnUrl },
      { name: 'rm', value: '2' },
      { name: 'cancel_return', value: returnUrl }
    ];

    console.warn('[PayPal][DEBUG] effectivePaymentMode:', this.effectivePaymentMode);
    console.warn('[PayPal][DEBUG] paypalActionUrl:', this.paypalActionUrl);
    console.warn('[PayPal][DEBUG] fields:', JSON.stringify(fields, null, 2));

    fields.forEach(field => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      form.appendChild(input);
    });

          document.body.appendChild(form);
          form.submit();
  }
}
